import { format, parseISO } from 'date-fns';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sprite from '../../../assets/icons/sprite.svg';
import { Story } from '../../../services';
import {
  getAriaSort,
  getCssVar,
  IconLabel,
  OrderDirection,
  sortByDateAsc,
  sortByStringAsc,
  useIsNarrowViewport,
} from '../../../shared';
import { Integration, IntegrationStatus, IntegrationType } from '../Integrations';

import classNames from 'classnames';
import classes from './IntegrationsTable.module.scss';

export enum StatusFilter {
  all = 'all',
  active = 'active',
  paused = 'paused',
}

enum IntegrationSortColumn {
  name = 'name',
  story = 'story',
  status = 'status',
  createdAt = 'createdAt',
}

interface SortHeaderArgs {
  column: IntegrationSortColumn;
  label: string;
}

interface IntegrationsTableProps {
  integrations: Integration[];
  stories: Story[];
  query: string;
  selectedIntegrationTypes: IntegrationType[];
  selectedStoryIds: number[];
  statusFilter: StatusFilter;
  onToggleStatus: (integration: Integration) => void;
  onUpdate: (integration: Integration) => void;
  onRemove: (integration: Integration) => void;
}

export const IntegrationsTable: FunctionComponent<IntegrationsTableProps> = ({
  integrations,
  stories,
  query,
  selectedIntegrationTypes,
  selectedStoryIds,
  statusFilter,
  onToggleStatus,
  onUpdate,
  onRemove,
}) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'integrations' });

  const isNarrowViewport = useIsNarrowViewport();

  const [sortColumn, setSortColumn] = useState<IntegrationSortColumn>(
    IntegrationSortColumn.createdAt
  );

  const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC);

  const formatDate = useCallback(
    (value?: string) => (value ? format(parseISO(value), 'dd.MM.yyyy, HH:mm') : ''),
    []
  );

  const onSortColumn = useCallback(
    (column: IntegrationSortColumn) => {
      if (column !== sortColumn) {
        setSortColumn(column);
        setOrderDirection(OrderDirection.ASC);
        return;
      }

      setOrderDirection((orderDirectionPrev) =>
        orderDirectionPrev === OrderDirection.ASC ? OrderDirection.DESC : OrderDirection.ASC
      );
    },
    [sortColumn]
  );

  const titleByStoryId = useMemo(
    () => new Map(stories.map(({ id, title }) => [id, title])),
    [stories]
  );

  const integrationsFiltered = useMemo<Integration[]>(() => {
    const q = query.trim().toLowerCase();

    return integrations.filter(({ type, status, name, original_url, mix_id }) => {
      if (selectedIntegrationTypes.length > 0 && !selectedIntegrationTypes.includes(type)) {
        return false;
      }

      if (selectedStoryIds.length > 0 && !selectedStoryIds.includes(mix_id)) {
        return false;
      }

      if (statusFilter !== StatusFilter.all && (status as string) !== statusFilter) {
        return false;
      }

      if (q && !name.toLowerCase().includes(q) && !original_url.toLowerCase().includes(q)) {
        return false;
      }

      return true;
    });
  }, [integrations, query, selectedIntegrationTypes, selectedStoryIds, statusFilter]);

  const integrationsDisplayed = useMemo<Integration[]>(() => {
    const direction = orderDirection === OrderDirection.ASC ? 1 : -1;

    return [...integrationsFiltered].sort(
      (
        { name: nameA, mix_id: mixIdA, status: statusA, created_at: createdAtA },
        { name: nameB, mix_id: mixIdB, status: statusB, created_at: createdAtB }
      ) => {
        switch (sortColumn) {
          case IntegrationSortColumn.name:
            return direction * sortByStringAsc(nameA, nameB);
          case IntegrationSortColumn.story: {
            const storyA = titleByStoryId.get(mixIdA) ?? '';
            const storyB = titleByStoryId.get(mixIdB) ?? '';
            return direction * sortByStringAsc(storyA, storyB);
          }
          case IntegrationSortColumn.status:
            return direction * sortByStringAsc(statusA, statusB);
          case IntegrationSortColumn.createdAt:
            return direction * sortByDateAsc(createdAtA, createdAtB);
          default:
            return 0;
        }
      }
    );
  }, [integrationsFiltered, sortColumn, orderDirection, titleByStoryId]);

  const sortHeader = ({ column, label }: SortHeaderArgs) => {
    const isSorted = column === sortColumn;

    return (
      <th
        className={classes['sort-header']}
        {...(isSorted && { 'aria-sort': getAriaSort(orderDirection) })}
      >
        <button
          type={'button'}
          className={classes['sort-header-content']}
          onClick={() => onSortColumn(column)}
        >
          {label}
          {isSorted && (
            <svg
              className={classNames(classes['sort-arrow'], {
                [classes['sort-arrow--up']]: orderDirection === OrderDirection.ASC,
              })}
            >
              <use href={`${sprite}#down-arrow`} />
            </svg>
          )}
        </button>
      </th>
    );
  };

  if (!integrationsDisplayed.length) {
    return (
      <div className={classes['wrapper']}>
        <div className={classes['empty']}>{ctxT('no-integrations-found')}</div>
      </div>
    );
  }

  return (
    <div className={classes['wrapper']}>
      <table
        className={classNames(classes['table'], {
          [classes['table--narrow']]: isNarrowViewport,
        })}
      >
        <thead>
          <tr>
            <th />
            {sortHeader({ column: IntegrationSortColumn.name, label: t('common.name') })}
            {sortHeader({ column: IntegrationSortColumn.story, label: t('common.story') })}
            <th>{t('common.url')}</th>
            {sortHeader({ column: IntegrationSortColumn.createdAt, label: t('common.created') })}
            <th>{ctxT('last-run-at')}</th>
            <th>{ctxT('last-successful-run-at')}</th>
            <th>{ctxT('last-error')}</th>
            <th>{ctxT('last-error-at')}</th>
            <th>{ctxT('cards-count')}</th>
            {sortHeader({ column: IntegrationSortColumn.status, label: t('common.status') })}
            <th />
          </tr>
        </thead>
        <tbody>
          {integrationsDisplayed.map((integration) => {
            const {
              id,
              name,
              type,
              original_url,
              status,
              mix_id,
              created_at,
              last_run_at,
              last_successful_run_at,
              last_error,
              last_error_at,
              cards_created_count,
            } = integration;

            return (
              <tr key={id}>
                <td>
                  <div className={classes['type-icon']}>
                    <IconLabel
                      iconId={type}
                      iconSize={16}
                      color={'#6b6d7b'}
                      singleColor
                      nonClickable
                    />
                  </div>
                </td>
                <td>
                  <button
                    type={'button'}
                    className={classes['name']}
                    onClick={() => onUpdate(integration)}
                  >
                    {name}
                  </button>
                </td>
                <td>
                  <div className={classes['story']}>{titleByStoryId.get(mix_id)}</div>
                </td>
                <td>
                  <a
                    href={original_url}
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    aria-label={original_url}
                  >
                    <IconLabel
                      iconId={'link'}
                      iconSize={16}
                      color={getCssVar('--base-link-text-color')}
                      hoverColor={getCssVar('--base-link-text-hover-color')}
                      singleColor
                    />
                  </a>
                </td>
                <td className={classes['date']}>{formatDate(created_at)}</td>
                <td className={classes['date']}>{formatDate(last_run_at)}</td>
                <td className={classes['date']}>{formatDate(last_successful_run_at)}</td>
                <td>
                  <div className={classes['last-error']}>{last_error}</div>
                </td>
                <td className={classes['date']}>{formatDate(last_error_at)}</td>
                <td>{cards_created_count}</td>
                <td>
                  <span className={classNames(classes['status'], classes[`status--${status}`])}>
                    {ctxT(status)}
                  </span>
                </td>
                <td>
                  <div className={classes['actions']}>
                    <IconLabel
                      iconSize={18}
                      iconId={status === IntegrationStatus.active ? 'stop' : 'run'}
                      ariaLabel={t(
                        `common.${status === IntegrationStatus.active ? 'pause' : 'resume'}`
                      )}
                      color={'#6b6e73'}
                      hoverColor={'#2d2d46'}
                      onClick={() => onToggleStatus(integration)}
                    />
                    <IconLabel
                      iconId={'edit'}
                      iconSize={18}
                      ariaLabel={t('common.edit')}
                      color={'#6b6e73'}
                      hoverColor={'#2d2d46'}
                      onClick={() => onUpdate(integration)}
                    />
                    <IconLabel
                      iconId={'delete'}
                      iconSize={18}
                      ariaLabel={t('common.delete')}
                      color={'#6b6e73'}
                      hoverColor={getCssVar('--color-danger-hover')}
                      onClick={() => onRemove(integration)}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
